import React, {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {useLocation, useNavigate} from 'react-router-dom'
import CourseOutcomeReport from '../../modules/course/CourseOutcomeReport'
import CourseOutcomeReportComponent from '../../modules/course/CourseOutcomeReportAI'
import {listExam} from '../my-course-exam/list/core/_requests'
import {
  loadCourseOutcomePerformance,
  toCourseOutcomeApiItems,
} from '../module-dashboard/courseOutcomePerformance'

type CourseOutcomeAverage = {
  courseOutcomeAverage: number
  courseOutcomeName: string
  [key: string]: unknown
}

type CourseReportLocationState = {
  id?: number | string | null
  examMergeMainSemesterCourseId?: number | string | null
  outcomeSourceSemesterCourseId?: number | string | null
}

const getRequestErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 403) {
      return 'Bu dersin öğrenme çıktısı raporunu görüntüleme yetkiniz bulunmuyor.'
    }

    return (
      error.response?.data?.message ||
      error.response?.data?.title ||
      'Dersin öğrenme çıktısı ortalamaları alınamadı.'
    )
  }

  return 'Dersin öğrenme çıktısı ortalamaları alınamadı.'
}

const CourseOutcomesReport: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const locationState = (location.state ?? null) as CourseReportLocationState | null

  const semesterCourseId = useMemo(() => {
    const candidate =
      locationState?.examMergeMainSemesterCourseId ??
      locationState?.outcomeSourceSemesterCourseId ??
      locationState?.id
    const parsed = Number(candidate)

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [locationState])

  const [courseOutcomeData, setCourseOutcomeData] = useState<Array<{value: number; label: string}>>(
    []
  )
  const [courseOutcomeAverages, setCourseOutcomeAverages] = useState<CourseOutcomeAverage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [calculationNote, setCalculationNote] = useState<string | null>(null)

  useEffect(() => {
    if (!semesterCourseId) {
      setCourseOutcomeData([])
      setCourseOutcomeAverages([])
      setLoading(false)
      setError(null)
      setCalculationNote(null)
      return
    }

    let active = true

    const fetchCourseOutcomeAverages = async () => {
      setLoading(true)
      setError(null)
      setCalculationNote(null)

      try {
        const exams = await listExam(semesterCourseId, 'page=1&items_per_page=9999').catch(() => [])
        const result = await loadCourseOutcomePerformance(semesterCourseId, exams)
        const averages = toCourseOutcomeApiItems(result.points) as CourseOutcomeAverage[]

        if (!active) return

        setCourseOutcomeData(
          averages.map((item) => ({
            value: Math.round(Number(item.courseOutcomeAverage || 0) * 100),
            label: item.courseOutcomeName,
          }))
        )
        setCourseOutcomeAverages(averages)
        if (result.source === 'exam-result-fallback') {
          setCalculationNote(
            `Ana dersin sınav sonuçlarından hesaplandı${
              result.partial ? ' · Bazı sınav verileri eksik' : ''
            }.`
          )
        }
      } catch (requestError) {
        if (!active) return

        setCourseOutcomeData([])
        setCourseOutcomeAverages([])
        setError(getRequestErrorMessage(requestError))
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchCourseOutcomeAverages()

    return () => {
      active = false
    }
  }, [semesterCourseId])

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/module-dashboard')
  }

  if (!semesterCourseId) {
    return (
      <div className='card'>
        <div className='card-body py-10 text-center'>
          <h1 className='fs-3 mb-3'>Çıktı raporu açılamadı</h1>
          <p className='text-muted mb-6'>
            Raporu görüntülemek için önce derslerinizden bir ders seçmeniz gerekiyor.
          </p>
          <button
            type='button'
            className='btn btn-primary'
            onClick={() => navigate('/my-course/list')}
          >
            Derslerime git
          </button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className='d-flex align-items-center justify-content-between flex-wrap gap-3 mb-5'>
        <div>
          <h1 className='fs-2 mb-1'>Öğrenme çıktısı raporu</h1>
          <p className='text-muted mb-0'>
            Dersin öğrenme çıktısı başarılarını inceleyin ve iyileştirme raporunu yönetin.
          </p>
        </div>
        <button type='button' className='btn btn-sm btn-light' onClick={goBack}>
          Geri dön
        </button>
      </div>

      {loading && (
        <div className='alert alert-info d-flex align-items-center' role='status'>
          <span className='spinner-border spinner-border-sm me-3' aria-hidden='true' />
          Öğrenme çıktısı ortalamaları yükleniyor…
        </div>
      )}

      {error && (
        <div className='alert alert-danger' role='alert'>
          {error}
        </div>
      )}

      {calculationNote && !error && (
        <div className='alert alert-light-primary' role='status'>
          {calculationNote}
        </div>
      )}

      {!loading && !error && courseOutcomeAverages.length === 0 && (
        <div className='alert alert-secondary' role='status'>
          Bu ders için hesaplanmış öğrenme çıktısı ortalaması bulunmuyor.
        </div>
      )}

      {!loading && courseOutcomeData.length > 0 && (
        <CourseOutcomeReport courseOutcomeData={courseOutcomeData} />
      )}

      <h2 className='mt-3 mb-3'>Öğrenme Çıktıları İyileştirme Raporu</h2>
      <CourseOutcomeReportComponent
        semesterCourseId={semesterCourseId}
        courseOutcomeAverages={courseOutcomeAverages}
      />
    </div>
  )
}

export default CourseOutcomesReport
